export type MediaStreamPair = {
  stream: MediaStream;
  audioStream: MediaStream;
};

/**
 * Merge a peer-lite `streamRemote` event into the player media streams.
 *
 * peer-lite emits `streamRemote` once per `ontrack`. Audio and video often
 * arrive in separate events (and a later video-only event must not wipe
 * audio attached earlier). When a kind is present on `incoming`, it replaces
 * that kind; when absent, the previous tracks for that kind are preserved.
 *
 * Always returns new MediaStream instances so UI listeners that compare by
 * object identity observe the update (in-place mutation of the same streams
 * can skip attaching late-arriving audio).
 */
export function mergeRemoteMediaStreams(
  incoming: MediaStream,
  current?: Partial<MediaStreamPair> | null,
): MediaStreamPair {
  const incomingVideo = incoming.getVideoTracks();
  const incomingAudio = incoming.getAudioTracks();
  const nextVideo =
    incomingVideo.length > 0 ? incomingVideo : (current?.stream?.getVideoTracks() ?? []);
  const nextAudio =
    incomingAudio.length > 0 ? incomingAudio : (current?.audioStream?.getAudioTracks() ?? []);

  const stream = new MediaStream();
  const audioStream = new MediaStream();
  for (const track of nextVideo) {
    stream.addTrack(track);
  }
  for (const track of nextAudio) {
    stream.addTrack(track);
    audioStream.addTrack(track);
  }
  return { stream, audioStream };
}
